import { CRASH_COUNTDOWN_BUILD_MS } from '../Sound/crashCountdownSprite'
import { COUNTDOWN_TRIGGER_MULTIPLIER } from './crashCountdownCue'
import { CRASH_RATE } from './crashCurve'

/**
 * The multiplier the round is at when the cue's BUILD sprite ends and the 3.5s
 * plateau starts looping — DERIVED, not chosen.
 *
 * The cue enters at 2x and the build runs a fixed 13.5s, so where that lands on
 * the curve falls straight out of the growth rate. Deriving it means a pacing
 * change (the curve has already been re-timed once, 120s -> 76.8s) moves the
 * volume shape with the sound instead of silently desynchronising them.
 */
export const COUNTDOWN_LOOP_ENTRY_MULTIPLIER =
  COUNTDOWN_TRIGGER_MULTIPLIER * Math.exp(CRASH_RATE * (CRASH_COUNTDOWN_BUILD_MS / 1000))

/**
 * Where the ease-back bottoms out: one further DOUBLING of the multiplier,
 * which is a fixed ~11.6s of real time whatever the entry point is. Long enough
 * that the settle is felt rather than heard as a fade.
 */
export const COUNTDOWN_GAIN_EASE_TO = COUNTDOWN_LOOP_ENTRY_MULTIPLIER * 2

/** The volume shape of the cue, as fractions of the effects volume. */
export interface CountdownGainProfile {
  /** Level the cue enters at, at 2x. Never zero — see DESKTOP_GAIN_PROFILE. */
  floor: number
  /** Level at the crescendo, where the plateau starts looping. */
  peak: number
  /** Level it settles to while the plateau loops. */
  sustain: number
  /** Fraction of the effects volume the cue may reach at its peak. */
  ceiling: number
}

/**
 * Desktop — tuned by ear over several live rounds.
 *
 * `floor` is deliberately NOT zero: a cue that fades up from silence reads as
 * broken audio rather than a subtle entrance. `peak` is below 1 because the
 * looping plateau is the loudest, densest part of the track, and handing over
 * at full made the very first loop land harder than the crescendo earned.
 */
export const DESKTOP_GAIN_PROFILE: CountdownGainProfile = {
  floor: 0.35,
  peak: 0.85,
  sustain: 0.5,
  ceiling: 0.4
}

/**
 * Mobile/tablet — LOUDER and, just as importantly, LESS DYNAMIC.
 *
 * With the shipped defaults (master 0.8 x effects 0.5 = 0.40) the desktop
 * profile puts the cue at 0.056 absolute on entry and 0.136 at its peak: about
 * -25dB to -17dB. Through desktop speakers that is the intended subtle
 * underscore. Through a phone speaker — which has almost no low-level
 * reproduction and no headroom, and is already carrying the livestream's own
 * music bed from the video element — the cue simply is not there.
 *
 * Raising the ceiling ALONE would not fix it, because what vanishes first is
 * the quiet entry. So the floor rises proportionally more than the peak: the
 * swell survives as a shape while every point on it clears the speaker's noise
 * floor. Peak lands at 0.30 absolute — comparable to the stream bed rather than
 * buried under it.
 */
export const MOBILE_GAIN_PROFILE: CountdownGainProfile = {
  floor: 0.55,
  peak: 1,
  sustain: 0.7,
  ceiling: 0.75
}

/**
 * How loud the rising-tension cue should be right now, as a fraction of the
 * EFFECTS volume — the profile's ceiling is already applied here, so the caller
 * multiplies by the effects volume and nothing else. Keeping the ceiling inside
 * this one tested function is what lets the mobile profile change BOTH the
 * shape and the level without a second, untested factor drifting elsewhere.
 *
 * Mapped against LOG(multiplier), not the multiplier itself. The crash curve is
 * exponential in time — `e^(ln100/76.8 · t)` — so log(multiplier) is LINEAR in
 * time. Ramping on the log therefore produces a swell that rises evenly by the
 * clock. Ramping on the raw multiplier would crawl for the first twenty seconds
 * and then lurch, because the multiplier accelerates.
 *
 * The shape follows the TRACK, not round numbers. The swell completes exactly
 * as the build sprite ends, so the cue reaches its peak on its own crescendo;
 * from there the track is looping a 3.5s plateau, and the gain eases back to a
 * bed so a long climb stays tense instead of becoming tiring.
 *
 * Quantised to thousandths so a 200ms poll does not publish a store update on
 * every single tick.
 */
export const resolveCountdownGain = (
  multiplier: number,
  profile: CountdownGainProfile = DESKTOP_GAIN_PROFILE
): number => {
  const { floor, peak, sustain, ceiling } = profile
  const scaled = (value: number) => Math.round(value * ceiling * 1000) / 1000

  if (Number.isNaN(multiplier)) return scaled(floor)
  if (multiplier <= COUNTDOWN_TRIGGER_MULTIPLIER) return scaled(floor)

  // Past the crescendo the plateau is looping — settle to a bed.
  if (multiplier > COUNTDOWN_LOOP_ENTRY_MULTIPLIER) {
    if (multiplier >= COUNTDOWN_GAIN_EASE_TO) return scaled(sustain)

    const easeSpan = Math.log(COUNTDOWN_GAIN_EASE_TO) - Math.log(COUNTDOWN_LOOP_ENTRY_MULTIPLIER)
    const eased = (Math.log(multiplier) - Math.log(COUNTDOWN_LOOP_ENTRY_MULTIPLIER)) / easeSpan

    return scaled(peak - eased * (peak - sustain))
  }

  const span = Math.log(COUNTDOWN_LOOP_ENTRY_MULTIPLIER) - Math.log(COUNTDOWN_TRIGGER_MULTIPLIER)
  const progress = (Math.log(multiplier) - Math.log(COUNTDOWN_TRIGGER_MULTIPLIER)) / span

  return scaled(floor + progress * (peak - floor))
}
